import { useState } from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Database, Copy, Lock, Eye, EyeOff, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { BACKEND_URL, API_URL } from '../config/api';

// BACKEND_URL imported from config/api

const DatabaseAccessPage = () => {
  const [pin, setPin] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [connectionString, setConnectionString] = useState("");
  const [showConnection, setShowConnection] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleUnlock = async () => {
    if (pin !== "1842") {
      toast.error("❌ Incorrect PIN");
      setPin("");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("jwtToken");
      const response = await axios.get(`${BACKEND_URL}/api/super-admin/db-connection`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setConnectionString(response.data.connection_string);
      setUnlocked(true);
      toast.success("✅ Unlocked!");
    } catch (error) {
      console.error("Error fetching connection string:", error);
      toast.error("Failed to fetch connection string");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(connectionString);
    toast.success("✅ Connection string copied to clipboard!");
  };

  const maskConnectionString = (str) => {
    if (!str) return "";
    // Show first 10 and last 10 characters, mask the middle
    if (str.length <= 20) return str;
    const start = str.substring(0, 10);
    const end = str.substring(str.length - 10);
    const middle = "*".repeat(str.length - 20);
    return `${start}${middle}${end}`;
  };

  if (!unlocked) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6">
        <div className="w-full max-w-md p-8 bg-white border border-gray-200 rounded-lg">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
              <Lock className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Database Access</h2>
            <p className="text-gray-600 mt-2">Enter PIN to view connection string</p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">PIN Code</label>
              <Input
                type="password"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleUnlock()}
                placeholder="Enter 4-digit PIN"
                className="text-center text-xl tracking-widest h-12"
                maxLength={4}
                autoFocus
              />
            </div>

            <Button
              onClick={handleUnlock}
              disabled={loading || pin.length !== 4}
              className="w-full h-12 bg-red-600 hover:bg-red-700 text-white font-semibold"
            >
              {loading ? "Unlocking..." : "Unlock"}
            </Button>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-xs text-yellow-800">
                <strong>Security Notice:</strong> This page contains sensitive database credentials. Only authorized super administrators should access this.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <div className="p-3 bg-green-100 rounded-lg">
          <CheckCircle className="w-6 h-6 text-green-600" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Database Access Unlocked</h2>
          <p className="text-gray-600">Production MongoDB Connection String</p>
        </div>
      </div>

      {/* Connection String Card */}
      <div className="p-6 border border-green-200 bg-green-50 rounded-lg">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <Database className="w-5 h-5 text-green-600" />
              Production Connection String
            </h3>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowConnection(!showConnection)}
              className="flex items-center gap-2"
            >
              {showConnection ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              {showConnection ? "Hide" : "Show"}
            </Button>
          </div>

          <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm break-all">
            {showConnection ? connectionString : maskConnectionString(connectionString)}
          </div>

          <Button
            onClick={copyToClipboard}
            className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold h-12"
          >
            <Copy className="w-5 h-5 mr-2" />
            Copy Full Connection String
          </Button>
        </div>
      </div>

      {/* Database Info */}
      <div className="p-6 bg-white border border-gray-200 rounded-lg">
        <h3 className="text-lg font-bold mb-4">Database Information</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded border border-gray-200">
            <span className="font-medium text-gray-700">Platform Database:</span>
            <span className="font-mono text-sm text-gray-900">bookingflows_platform</span>
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="p-6 bg-blue-50 border border-blue-200 rounded-lg">
        <h3 className="text-lg font-bold mb-3 text-gray-900">How to Connect with MongoDB Compass</h3>
        <ol className="space-y-2 text-sm text-gray-700">
          <li className="flex items-start gap-2">
            <span className="font-bold">1.</span>
            <span>Download and install MongoDB Compass from <a href="https://www.mongodb.com/products/compass" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">mongodb.com/products/compass</a></span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-bold">2.</span>
            <span>Open MongoDB Compass</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-bold">3.</span>
            <span>Click "Copy Full Connection String" above</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-bold">4.</span>
            <span>Paste the connection string into Compass and click "Connect"</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-bold">5.</span>
            <span>You'll see all databases and can browse/edit data</span>
          </li>
        </ol>
      </div>

      {/* Warning */}
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-sm text-red-900">
          <strong>CRITICAL WARNING:</strong> This connection string provides full access to the production database. 
          Always backup before making changes. Never share this connection string with unauthorized users.
        </p>
      </div>
    </div>
  );
};

export default DatabaseAccessPage;
